import { useEffect, useRef, useState } from "react";
import { Modal } from "./Modal";

function fmtDateTime(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function UnblockHoardingModal({ hoarding, onClose, onConfirm }) {
  const holdTimerRef = useRef(null);
  const progressTimerRef = useRef(null);
  const [progress, setProgress] = useState(0);
  const [holding, setHolding] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => () => {
    window.clearTimeout(holdTimerRef.current);
    window.clearInterval(progressTimerRef.current);
  }, []);

  async function confirmUnblock() {
    if (loading) return;
    setLoading(true);
    try {
      await onConfirm();
      onClose();
    } catch {
      setProgress(0);
    } finally {
      setLoading(false);
    }
  }

  function startHold(event) {
    if (loading) return;
    event.preventDefault();
    setHolding(true);
    setProgress(0);
    const startedAt = Date.now();
    window.clearTimeout(holdTimerRef.current);
    window.clearInterval(progressTimerRef.current);
    progressTimerRef.current = window.setInterval(() => {
      setProgress(Math.min(100, ((Date.now() - startedAt) / 3000) * 100));
    }, 40);
    holdTimerRef.current = window.setTimeout(() => {
      window.clearInterval(progressTimerRef.current);
      setProgress(100);
      confirmUnblock();
    }, 3000);
  }

  function cancelHold() {
    if (!holding || loading) return;
    window.clearTimeout(holdTimerRef.current);
    window.clearInterval(progressTimerRef.current);
    setHolding(false);
    setProgress(0);
  }

  return (
    <Modal title="Confirm Unblock" onClose={onClose} accent="rose">
      <div className="space-y-4">
        <div className="rounded-lg border border-rose-100 bg-rose-50 p-3">
          <div className="text-xs font-bold uppercase tracking-wide text-rose-600">Blocked Hoarding</div>
          <div className="mt-1 text-sm font-semibold text-rose-950">{hoarding?.hoarding_id || hoarding?.id}</div>
          <div className="mt-2 text-xs font-semibold text-rose-700">Until {fmtDateTime(hoarding?.blocked_until)}</div>
          <div className="mt-3 rounded-md bg-white/75 p-3 text-sm text-rose-900">
            {hoarding?.blocked_reason || "No reason recorded."}
          </div>
        </div>

        <div>
          <div className="mb-3 text-center text-xs font-bold uppercase tracking-wide text-slate-500">
            Hold for 3 seconds to unblock
          </div>
          <div className="flex justify-center">
            <button
              type="button"
              disabled={loading}
              onPointerDown={startHold}
              onPointerUp={cancelHold}
              onPointerLeave={cancelHold}
              onPointerCancel={cancelHold}
              className="relative flex h-32 w-32 select-none items-center justify-center rounded-full text-sm font-black text-white outline-none transition active:scale-[0.98] disabled:opacity-75"
              aria-label="Hold for three seconds to confirm unblock"
            >
              <svg className="absolute inset-0 h-full w-full -rotate-90" viewBox="0 0 120 120" aria-hidden="true">
                <circle cx="60" cy="60" r="52" fill="none" stroke="#fee2e2" strokeWidth="8" />
                <circle
                  cx="60"
                  cy="60"
                  r="52"
                  fill="none"
                  stroke="#e11d48"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${2 * Math.PI * 52}`}
                  strokeDashoffset={`${2 * Math.PI * 52 * (1 - progress / 100)}`}
                />
              </svg>
              <span className="absolute inset-4 flex items-center justify-center rounded-full bg-rose-600 px-4 text-center shadow-lg shadow-rose-500/25">
                {loading ? "Unblocking..." : holding ? `${Math.ceil((100 - progress) * 0.03)}s` : "Hold Confirm"}
              </span>
            </button>
          </div>
        </div>

        <button type="button" onClick={onClose} disabled={loading} className="hx-secondary-action w-full">
          Cancel
        </button>
      </div>
    </Modal>
  );
}
